"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
// From ./shared, not the module index: the index imports `db`, which would put
// pg (and its fs/net/tls requires) into this client bundle.
import type { SitePromoRecord } from "@/server/modules/site-promos/shared";

/**
 * Content, media and copy all come from the SitePromo table.
 *
 * Colour comes entirely from the theme tokens (--surface, --foreground,
 * --muted, --cta), so the panel is light in the light theme and dark in the
 * dark one with no per-theme branches here. The fixed --on-dark* tokens are
 * deliberately NOT used: those exist for permanently-dark surfaces like the
 * hero scrims and map chips, and this panel is not one.
 */

/** Anything not resolvable inside this app — a campaign form, a partner site. */
function isExternalHref(href: string): boolean {
  return /^(https?:)?\/\//i.test(href.trim());
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, video[controls], [tabindex]:not([tabindex="-1"])';

/**
 * Presentational promo modal: focus trap, Escape, scroll lock, leave
 * animation. It renders whatever promo it is handed and reports closes.
 *
 * It deliberately owns NO frequency or route logic. Deciding whether a promo
 * may appear at all lives in HomePromoController + lib/promo-frequency.ts, so
 * there is exactly one place that can open a promotional interruption. This
 * component used to make that decision itself, which is how the slogan
 * campaign ended up opening on /tours and /book.
 */
export function SitePromoModal({
  promo,
  onClose,
}: {
  /** Null renders nothing. */
  promo: SitePromoRecord | null;
  /** Fired once the leave animation has played. */
  onClose: () => void;
}) {
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setClosing(true);
    // Matches the leave animation; the panel unmounts once it has played.
    window.setTimeout(() => {
      setClosing(false);
      onClose();
    }, 180);
  }, [onClose]);

  // Escape to close, Tab confined to the panel, focus moved in and then back.
  useEffect(() => {
    if (!promo) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>("[data-promo-initial-focus]")?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Locking the body prevents the page from scrolling behind the panel;
    // the padding swap keeps the layout from shifting as the scrollbar goes.
    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      restoreFocusTo.current?.focus?.();
    };
  }, [promo, close]);

  if (!promo) return null;

  const headingId = `promo-heading-${promo.key}`;
  const bodyId = `promo-body-${promo.key}`;
  const state = closing ? "closing" : "open";

  return (
    <div
      className="pbd-promo-backdrop"
      data-state={state}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={bodyId}
        className="pbd-promo-panel"
        data-state={state}
      >
        <button type="button" onClick={close} aria-label="Close" className="pbd-promo-close">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {promo.mediaType === "image" && promo.mediaUrl ? (
          // A plain <img>: mediaUrl may be an uploaded path or an external URL
          // an editor pasted, and next/image would reject hosts absent from
          // remotePatterns at render time.
          // eslint-disable-next-line @next/next/no-img-element
          <img src={promo.mediaUrl} alt={promo.mediaAlt} className="pbd-promo-media" />
        ) : null}

        {promo.mediaType === "video" && promo.mediaUrl ? (
          <video
            src={promo.mediaUrl}
            className="pbd-promo-media"
            controls
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={promo.mediaAlt || undefined}
          />
        ) : null}

        <div className="pbd-promo-body">
          {promo.mediaType === "none" ? (
            <span className="pbd-promo-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.2l5.9-.9L12 3z" />
              </svg>
            </span>
          ) : null}

          {promo.eyebrow ? <p className="pbd-promo-eyebrow">{promo.eyebrow}</p> : null}

          <h2 id={headingId} className="pbd-promo-heading">
            {promo.heading}
          </h2>

          <p id={bodyId} className="pbd-promo-text">
            {promo.body}
          </p>

          <div className="pbd-promo-actions">
            <button type="button" onClick={close} className="pbd-promo-secondary">
              {promo.secondaryLabel}
            </button>
            {/* An external primaryHref (a Google Form, a partner site) opens
                in a new tab: the promo is an aside, and sending someone off
                the site to fill in a form would otherwise cost them the page
                they were reading. Internal hrefs keep using Link so they stay
                client-side navigations. */}
            {isExternalHref(promo.primaryHref) ? (
              <a
                href={promo.primaryHref}
                target="_blank"
                rel="noopener noreferrer"
                data-promo-initial-focus
                className="pbd-promo-primary"
              >
                {promo.primaryLabel}
              </a>
            ) : (
              <Link
                href={promo.primaryHref}
                data-promo-initial-focus
                className="pbd-promo-primary"
              >
                {promo.primaryLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
